
import { Card } from "@/components/ui/card";
import { Vaga } from "@/types/vaga";
import { VagaCardMetricas, StatusNegociacaoFreelancer } from "@/types/kanban";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Building2, Briefcase, DollarSign, MoreVertical, Pencil, Trash2, ArrowRight, User, Clock, AlertCircle, FileText, CalendarClock, Users, CheckSquare, Send, Handshake, FolderOpen, CalendarDays, Puzzle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useInternalLink } from "@/hooks/use-internal-link";
import { Link, useNavigate } from "react-router";
import { VAGA_STATUS_COLUMNS } from "@/utils/vaga-status";
import { differenceInDays } from "date-fns";
import { CONTROLE_PROJETO_STATUS_LABEL, type ControleProjetoStatus } from "@/types/controle-projeto";
import { getPropostaStatusInfo } from "@/utils/proposta-status";
import { useCriarProposta } from "@/hooks/use-finance-propostas";
import { useToast } from "@/hooks/use-toast";
import { formatApiError } from "@/lib/api-error";
import type { PropostaPorProjetoItem } from "@/types/finance";
import { useVoltarParaRascunho } from "@/hooks/use-projeto-rascunho";
import type { RascunhoCancelado } from "@/services/projeto-rascunho-api";

interface VagaProjetoResumo {
  id: string;
  status: ControleProjetoStatus;
  valor_projeto?: number | null;
  data_inicio?: string | null;
  freelancer_id?: string | null;
  freelancer_nome?: string | null;
}

const PROJETO_STATUS_BADGE_CLASS: Record<ControleProjetoStatus, string> = {
  ativo: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  encerrado: 'bg-slate-100 text-slate-600 border-slate-200',
  renovado: 'bg-violet-50 text-violet-700 border-violet-200',
  rascunho: 'bg-amber-50 text-amber-700 border-amber-200',
  planejado: 'bg-sky-50 text-sky-700 border-sky-200',
};

interface VagaCardProps {
  vaga: Vaga;
  onDragStart: (e: React.DragEvent, vagaId: string) => void;
  onClick: () => void;
  onResponsavelChange: (responsavel: string) => void;
  onEdit?: (vagaId: string) => void;
  onDelete?: (vagaId: string) => void;
  onMoveToStatus?: (vagaId: string, status: Vaga["status"]) => void;
  onRequestReprovacao?: (vagaId: string) => void;
  onRequestAprovacao?: (vagaId: string) => void;
  onEditProjeto?: (vagaId: string) => void;
  usuarios: { id: string; full_name?: string; username?: string }[];
  metricas?: VagaCardMetricas;
  projeto?: VagaProjetoResumo;
  /** Draft cancelled while the vaga stays in Aprovado; only when there is no live `projeto`. */
  rascunhoCancelado?: RascunhoCancelado;
  freelancerAprovadoKanbanNome?: string | null;
  freelancerAprovadoKanbanId?: string | null;
  /**
   * Proposta ligada ao `projeto.id`, já resolvida em lote pelo componente pai
   * (ver KanbanColumn) via `usePropostasPorProjeto` — nunca uma chamada por
   * card. `undefined` = ainda não buscada (ou usuário sem a tela `financeiro`,
   * caso em que o pai nem dispara a busca); `null` = buscada e não existe.
   */
  proposta?: PropostaPorProjetoItem | null;
}

const NEG_LABEL_SHORT: Record<StatusNegociacaoFreelancer, string> = {
  oferta_feita: "Oferta",
  topou: "Topou",
  contra_proposta: "Contra-prop.",
  contra_proposta_aceita: "C-prop. ✓",
  aguardando_resposta_agencia: "Aguard. ag.",
  recusou: "Recusou",
  desistiu: "Desistiu",
};

const NEG_LABEL_FULL: Record<StatusNegociacaoFreelancer, string> = {
  oferta_feita: "Oferta feita",
  topou: "Topou",
  contra_proposta: "Contra-proposta",
  contra_proposta_aceita: "Contra-proposta aceita",
  aguardando_resposta_agencia: "Aguardando resposta da agência",
  recusou: "Recusou",
  desistiu: "Desistiu",
};

const formatBRLCentavos = (centavos?: number | null): string => {
  if (centavos == null) return "";
  return (centavos / 100).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
};

const NEG_BADGE_COLOR: Record<StatusNegociacaoFreelancer, string> = {
  oferta_feita: "bg-blue-50 text-blue-700 border-blue-200",
  topou: "bg-green-50 text-green-700 border-green-200",
  contra_proposta: "bg-amber-50 text-amber-700 border-amber-200",
  contra_proposta_aceita: "bg-emerald-50 text-emerald-700 border-emerald-200",
  aguardando_resposta_agencia: "bg-slate-50 text-slate-700 border-slate-200",
  recusou: "bg-red-50 text-red-700 border-red-200",
  desistiu: "bg-red-50 text-red-700 border-red-200",
};

// Fonte única das etapas do board — ver src/utils/vaga-status.ts.
const statusOptions = VAGA_STATUS_COLUMNS;

export function VagaCard({
  vaga,
  onDragStart,
  onClick,
  onResponsavelChange,
  onEdit,
  onDelete,
  onMoveToStatus,
  onRequestReprovacao,
  onRequestAprovacao,
  onEditProjeto,
  usuarios,
  metricas,
  projeto,
  rascunhoCancelado,
  freelancerAprovadoKanbanNome,
  freelancerAprovadoKanbanId,
  proposta,
}: VagaCardProps) {
  const linkProps = useInternalLink();
  const cardHref = `/vagas/${vaga.id}`;
  const responsavelUsuario = usuarios.find(u => u.id === vaga.responsavel);
  const navigate = useNavigate();
  const { toast } = useToast();
  const criarProposta = useCriarProposta();
  const voltarParaRascunho = useVoltarParaRascunho();

  const handleVoltarParaRascunho = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!rascunhoCancelado) return;
    try {
      await voltarParaRascunho.mutateAsync(rascunhoCancelado.projeto_id);
      toast({ title: "Projeto de volta para rascunho" });
    } catch (err) {
      const formatted = formatApiError(err);
      toast({ variant: "destructive", title: formatted.title, description: formatted.detail });
    }
  };

  const handleGerarProposta = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!projeto || !vaga.agencia_id) return;
    try {
      const nova = await criarProposta.mutateAsync({
        agencia_id: vaga.agencia_id,
        controle_projeto_ids: [projeto.id],
        origem: "vaga",
        vaga_id: vaga.id,
      });
      navigate(`/financeiro/propostas/${nova.id}`);
    } catch (err) {
      const formatted = formatApiError(err);
      toast({ variant: "destructive", title: formatted.title, description: formatted.detail });
    }
  };
  
  // Calcula tempo na etapa atual. Retorna versão curta (chip do header) e
  // versão longa (tooltip) — chip precisa caber em coluna de 320px.
  const getStatusDuration = (lastStatusChanged: string | null | undefined): { short: string; long: string } => {
    if (!lastStatusChanged) return { short: "", long: "" };

    const now = new Date();
    const statusChangeDate = new Date(lastStatusChanged);
    const diffMs = now.getTime() - statusChangeDate.getTime();

    const minutes = Math.floor(diffMs / (1000 * 60));
    const hours = Math.floor(diffMs / (1000 * 60 * 60));
    const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    const weeks = Math.floor(days / 7);
    const months = Math.floor(days / 30);

    if (months > 0) return { short: `${months}mes`, long: `${months} mes${months === 1 ? '' : 'es'}` };
    if (weeks > 0) return { short: `${weeks}sem`, long: `${weeks} semana${weeks === 1 ? '' : 's'}` };
    if (days > 0) return { short: `${days}d`, long: `${days} dia${days === 1 ? '' : 's'}` };
    if (hours > 0) return { short: `${hours}h`, long: `${hours} hora${hours === 1 ? '' : 's'}` };
    return { short: `${minutes}min`, long: `${minutes} min` };
  };

  const statusDuration = getStatusDuration(vaga.last_status_changed_at);
  
  // Get badge color based on duration
  const getStatusBadgeColor = (lastStatusChanged: string | null | undefined): string => {
    if (!lastStatusChanged) return "bg-blue-50 text-blue-700 border-blue-200";
    
    const now = new Date();
    const statusChangeDate = new Date(lastStatusChanged);
    const diffMs = now.getTime() - statusChangeDate.getTime();
    const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    const weeks = Math.floor(days / 7);
    const months = Math.floor(days / 30);
    
    if (months >= 1) return "bg-red-50 text-red-700 border-red-200";
    if (weeks >= 1) return "bg-orange-50 text-orange-700 border-orange-200";
    return "bg-blue-50 text-blue-700 border-blue-200";
  };

  const statusBadgeColor = getStatusBadgeColor(vaga.last_status_changed_at);
  const shouldShowStatusDuration = !!statusDuration.short && vaga.status !== "aprovado" && vaga.status !== "reprovado";
  
  // Format verba range
  const formatVerba = (): string => {
    if (vaga.verba_minima && vaga.verba_maxima) {
      const min = (vaga.verba_minima / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
      const max = (vaga.verba_maxima / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
      return `${min} - ${max}`;
    }
    if (vaga.verba_minima) {
      return `A partir de ${(vaga.verba_minima / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}`;
    }
    if (vaga.verba_maxima) {
      return `Até ${(vaga.verba_maxima / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}`;
    }
    return vaga.verba || "";
  };

  // Helpers para campos novos do card (deadline + projeto)
  const formatDeadline = (iso: string | null | undefined): string => {
    if (!iso) return '';
    const d = new Date(iso + 'T00:00:00');
    const days = differenceInDays(d, new Date());
    const fmt = d.toLocaleDateString('pt-BR');
    if (days < 0) return `Atrasado · ${fmt}`;
    if (days === 0) return `Hoje · ${fmt}`;
    if (days === 1) return `Amanhã · ${fmt}`;
    return `Em ${days}d · ${fmt}`;
  };

  const getDeadlineColor = (iso: string | null | undefined): string => {
    if (!iso) return 'bg-slate-50 text-slate-600 border-slate-200';
    const d = new Date(iso + 'T00:00:00');
    const days = differenceInDays(d, new Date());
    if (days < 0) return 'bg-red-50 text-red-700 border-red-200';
    if (days <= 3) return 'bg-orange-50 text-orange-700 border-orange-200';
    if (days <= 7) return 'bg-amber-50 text-amber-700 border-amber-200';
    return 'bg-slate-50 text-slate-700 border-slate-200';
  };

  const formatCentavosBRL = (centavos: number): string =>
    (centavos / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

  // controle_projetos.valor_projeto é armazenado em reais (não centavos) —
  // ver AprovacaoModal/ControleProjetoForm. Por isso helper dedicado sem /100.
  const formatReaisBRL = (reais: number): string =>
    reais.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

  const formatDateBR = (iso: string): string => {
    const d = new Date(iso + 'T00:00:00');
    return d.toLocaleDateString('pt-BR');
  };

  // Cor da barra do topo por etapa do kanban — substitui o gradiente decorativo.
  const getEtapaBarColor = (status: Vaga["status"]): string => {
    const map: Record<Vaga["status"], string> = {
      entrada: "bg-slate-300",
      triagem: "bg-blue-400",
      shortlist: "bg-blue-500",
      checagem_disponibilidade: "bg-indigo-500",
      validacao: "bg-purple-500",
      aprovado: "bg-emerald-500",
      reprovado: "bg-red-400",
    };
    return map[status] ?? "bg-slate-300";
  };

  // Format tipo de reprovação label
  const getTipoReprovacaoLabel = (tipo: string | null | undefined): string => {
    if (!tipo) return "";
    
    const labels: Record<string, string> = {
      'corisco_falta_perfis_adequados': '[CORISCO] Vaga declinada por falta de perfis adequados no banco de talentos',
      'corisco_falta_disponibilidade_perfis': '[CORISCO] Vaga declinada por falta de disponibilidade dos perfis selecionados',
      'corisco_indicacoes_fora_prazo': '[CORISCO] Vaga caiu, pois as indicações não foram feitas em tempo hábil',
      'cliente_perfis_nao_aprovados': '[CLIENTE] Vaga caiu, pois os perfis indicados não foram aprovados pelo cliente',
      'cliente_sem_resposta_perfis': '[CLIENTE] Vaga caiu, pois não houve resposta por parte do cliente sobre os perfis indicados',
      'cliente_motivos_alheios_indicacoes': '[CLIENTE] Vaga caiu, por motivos alheios às indicações',
    };
    
    return labels[tipo] || tipo;
  };
  
  return (
    <Card
      className="group cursor-pointer hover:shadow-lg transition-all duration-300 border-0 bg-white/50 backdrop-blur-xs hover:bg-white/80 relative overflow-hidden"
      draggable
      onDragStart={(e) => onDragStart(e, vaga.id)}
      {...linkProps(cardHref, () => onClick())}
    >
      {/* Barra fina do topo, cor por etapa */}
      <div className={`absolute top-0 left-0 w-full h-0.5 ${getEtapaBarColor(vaga.status)}`} />

      {/* Zona 1 — Header: id · modalidade à esquerda; tempo na etapa sempre à direita */}
      <div className="flex items-center justify-between p-3 pb-2 gap-2">
        <div className="flex items-center gap-2 min-w-0 flex-1">
          <Badge variant="outline" className="text-xs font-mono bg-muted/50 shrink-0">
            #{vaga.id.slice(0, 3)}
          </Badge>
          {vaga.modalidade && (
            <>
              <span className="text-slate-300 shrink-0">·</span>
              <Badge
                variant="outline"
                className="text-[10px] px-1.5 py-0 h-5 shrink-0 bg-muted/50 text-slate-600"
              >
                <Puzzle className="h-3 w-3 mr-0.5 text-slate-400" />
                {vaga.modalidade === "frila" ? "Frila" : "Fixa"}
              </Badge>
            </>
          )}
        </div>

        {shouldShowStatusDuration && (
          <Badge
            variant="secondary"
            className={`text-[10px] px-1.5 py-0 h-5 shrink-0 ${statusBadgeColor}`}
            title={`Há ${statusDuration.long} nesta etapa`}
          >
            <Clock className="h-3 w-3 mr-0.5" />
            {statusDuration.short}
          </Badge>
        )}

        <div onClick={(e) => e.stopPropagation()} className="shrink-0">
          <DropdownMenu>
            {/* Só existe no hover (ou aberto): o chip de tempo encosta na borda
                direita e é empurrado pra esquerda quando o menu aparece */}
            {/* h-5 = mesma altura dos chips do header: aparecer no hover não pode
                mudar a altura da linha (o card crescia uns px) */}
            <DropdownMenuTrigger className="hidden group-hover:inline-flex data-[state=open]:inline-flex h-5 w-5 items-center justify-center hover:bg-accent/50 rounded-full">
              <MoreVertical className="h-4 w-4 text-muted-foreground" />
            </DropdownMenuTrigger>
          <DropdownMenuContent>
            {onEdit && (
              <DropdownMenuItem
                onClick={() => onEdit(vaga.id)}
                className="flex items-center gap-2 cursor-pointer"
              >
                <Pencil className="h-4 w-4" />
                Editar
              </DropdownMenuItem>
            )}
            
            {onMoveToStatus && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuSub>
                  <DropdownMenuSubTrigger className="flex items-center gap-2 cursor-pointer">
                    <ArrowRight className="h-4 w-4" />
                    Enviar para...
                  </DropdownMenuSubTrigger>
                  <DropdownMenuSubContent>
                    {statusOptions
                      .filter(option => option.id !== vaga.status)
                      .map((option) => (
                        <DropdownMenuItem
                          key={option.id}
                          onClick={() => {
                            if (option.id === 'reprovado' && onRequestReprovacao) {
                              onRequestReprovacao(vaga.id);
                            } else if (option.id === 'aprovado' && onRequestAprovacao) {
                              onRequestAprovacao(vaga.id);
                            } else {
                              onMoveToStatus(vaga.id, option.id as Vaga["status"]);
                            }
                          }}
                          className="cursor-pointer"
                        >
                          {option.title}
                        </DropdownMenuItem>
                      ))}
                  </DropdownMenuSubContent>
                </DropdownMenuSub>
              </>
            )}
            
            {onDelete && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={() => onDelete(vaga.id)}
                  className="flex items-center gap-2 cursor-pointer text-destructive focus:text-destructive"
                >
                  <Trash2 className="h-4 w-4" />
                  Excluir
                </DropdownMenuItem>
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
        </div>
      </div>

      {/* Conteúdo Principal */}
      <div className="px-3 pb-3 space-y-3">
        {/* Zona 2 — Identidade: agência, área, valor, deadline */}
        <div className="space-y-1.5">
          {/* Agência + formato (remoto/híbrido/presencial: aqui sempre tem espaço) */}
          <div className="flex items-center gap-2 min-w-0">
            <Building2 className="h-4 w-4 text-blue-600 shrink-0" />
            <span className="font-semibold text-sm truncate">{vaga.agencia}</span>
            {vaga.formato && (
              <span className="text-xs text-slate-500 truncate shrink-0" title={vaga.formato}>
                · {vaga.formato}
              </span>
            )}
          </div>

          {/* Área de Atuação + descrição curta (diferencia vagas parecidas) */}
          <div className="flex items-center gap-2 min-w-0">
            <Briefcase className="h-3.5 w-3.5 text-slate-500 shrink-0" />
            <span className="text-xs text-slate-600 truncate shrink-0 max-w-full">
              {vaga.areas || "Área não especificada"}
            </span>
            {vaga.descricao_curta && (
              <span className="text-xs text-slate-400 truncate" title={vaga.descricao_curta}>
                · {vaga.descricao_curta}
              </span>
            )}
          </div>

          {/* Valor — linha tipográfica em vez de badge colorido */}
          {formatVerba() && (
            <div className="flex items-center gap-2">
              <DollarSign className="h-3.5 w-3.5 text-emerald-600 shrink-0" />
              <span className="text-sm font-semibold text-slate-800 truncate">
                {formatVerba()}
              </span>
            </div>
          )}

          {/* Deadline — mantém badge colorido (alerta semântico) */}
          {vaga.deadline_preenchimento && (
            <div>
              <Badge
                variant="secondary"
                className={`text-xs ${getDeadlineColor(vaga.deadline_preenchimento)}`}
              >
                <CalendarClock className="h-3 w-3 mr-1" />
                {formatDeadline(vaga.deadline_preenchimento)}
              </Badge>
            </div>
          )}
        </div>

        {/* Zona 3 — Estado do funil (container destacado em slate-50) */}
        {vaga.status !== 'reprovado' && metricas && (
          <section className="bg-slate-50/70 rounded-md p-2.5 space-y-2">
            {/* Header da seção — varia por etapa */}
            <div className="flex items-center justify-between gap-2">
              <p className="text-[10px] uppercase tracking-wide text-slate-500 flex items-center gap-1">
                {vaga.status === 'aprovado' ? (
                  <><FolderOpen className="h-3 w-3" /> Projeto vinculado</>
                ) : vaga.status === 'validacao' ? (
                  <><Send className="h-3 w-3" /> Envio pra agência</>
                ) : vaga.status === 'checagem_disponibilidade' ? (
                  <><Handshake className="h-3 w-3" /> Negociações + seleção</>
                ) : (
                  <><Users className="h-3 w-3" /> Kanban interno</>
                )}
              </p>
              {vaga.status === 'aprovado' && projeto && onEditProjeto && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onEditProjeto(vaga.id);
                  }}
                  className="shrink-0 inline-flex items-center gap-1 text-[10px] font-medium text-slate-500 hover:text-slate-900 transition-colors"
                  title="Editar valor e dados do projeto"
                >
                  <Pencil className="h-3 w-3" />
                  Editar
                </button>
              )}
            </div>

            {/* Base comum: contagens do kanban interno (não mostra em Aprovado) */}
            {vaga.status !== 'aprovado' && (
              <div className="flex flex-wrap gap-1.5">
                {(metricas.qtd_selecionados_kanban ?? 0) > 0 && (
                  <Badge
                    variant="outline"
                    className="text-[10px] bg-white border-slate-200 text-slate-700"
                    title="Freelancers na etapa Selecionados do kanban interno"
                  >
                    <CheckSquare className="h-3 w-3 mr-1 text-indigo-500" />
                    Selecionados <span className="font-semibold text-slate-900 ml-1">{metricas.qtd_selecionados_kanban}</span>
                  </Badge>
                )}
                <Badge
                  variant="outline"
                  className="text-[10px] bg-white border-slate-200 text-slate-700"
                  title="Freelancers na Shortlist"
                >
                  <Users className="h-3 w-3 mr-1 text-blue-500" />
                  Shortlist <span className="font-semibold text-slate-900 ml-1">{metricas.qtd_shortlist}</span>
                </Badge>
                {metricas.qtd_aprovados_kanban > 0 && (
                  <Badge
                    variant="outline"
                    className="text-[10px] bg-emerald-50 text-emerald-700 border-emerald-200"
                    title="Aprovados no kanban interno"
                  >
                    Aprovados <span className="font-semibold ml-1">{metricas.qtd_aprovados_kanban}</span>
                  </Badge>
                )}
                {metricas.qtd_reprovados_kanban > 0 && (
                  <Badge
                    variant="outline"
                    className="text-[10px] bg-white border-slate-200 text-slate-600"
                    title="Reprovados no kanban interno"
                  >
                    Reprov. <span className="font-semibold ml-1">{metricas.qtd_reprovados_kanban}</span>
                  </Badge>
                )}
              </div>
            )}

            {/* Checagem de Disponibilidade — negociações + selecionados */}
            {vaga.status === 'checagem_disponibilidade' && (
              <>
                {Object.keys(metricas.negociacao_por_status || {}).length > 0 && (() => {
                  const detalhes = metricas.negociacao_detalhes ?? [];
                  const badges = (
                    <div className="flex flex-wrap gap-1">
                      {(Object.entries(metricas.negociacao_por_status) as [StatusNegociacaoFreelancer, number][]).map(
                        ([status, n]) => (
                          <Badge
                            key={status}
                            variant="outline"
                            className={`text-[10px] ${NEG_BADGE_COLOR[status]}`}
                          >
                            {NEG_LABEL_SHORT[status]} <span className="font-semibold ml-1">{n}</span>
                          </Badge>
                        )
                      )}
                    </div>
                  );
                  // Sem detalhe por freelancer (ex.: dado antigo) — só os badges.
                  if (detalhes.length === 0) return badges;
                  return (
                    <TooltipProvider delayDuration={150}>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <div className="cursor-help">{badges}</div>
                        </TooltipTrigger>
                        <TooltipContent side="bottom" align="start" className="max-w-xs p-0">
                          <div className="divide-y divide-slate-100">
                            {detalhes.map((d, i) => (
                              <div key={i} className="px-2.5 py-1.5 text-xs">
                                <div className="flex items-center justify-between gap-2">
                                  <span className="font-medium text-slate-900 truncate">{d.nome || 'Sem nome'}</span>
                                  <span className={`shrink-0 text-[10px] px-1.5 py-0.5 rounded border ${NEG_BADGE_COLOR[d.status]}`}>
                                    {NEG_LABEL_FULL[d.status]}
                                  </span>
                                </div>
                                {(d.valor != null || d.data_disponibilidade) && (
                                  <div className="mt-0.5 text-[11px] text-slate-500 flex flex-wrap gap-x-2">
                                    {d.valor != null && <span>{formatBRLCentavos(d.valor)}</span>}
                                    {d.data_disponibilidade && <span>disp. {formatDateBR(d.data_disponibilidade)}</span>}
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  );
                })()}
                {metricas.qtd_selecionados_agencia > 0 && (
                  <Badge
                    variant="outline"
                    className="text-[10px] bg-white border-slate-200 text-slate-700"
                  >
                    <CheckSquare className="h-3 w-3 mr-1 text-indigo-500" />
                    Selecionados p/ agência <span className="font-semibold text-slate-900 ml-1">{metricas.qtd_selecionados_agencia}</span>
                  </Badge>
                )}
              </>
            )}

            {/* Validação — selecionados / enviados */}
            {vaga.status === 'validacao' && (
              <div className="flex flex-wrap gap-1.5">
                <Badge
                  variant="outline"
                  className="text-[10px] bg-white border-slate-200 text-slate-700"
                >
                  <CheckSquare className="h-3 w-3 mr-1 text-indigo-500" />
                  Selecionados <span className="font-semibold text-slate-900 ml-1">{metricas.qtd_selecionados_agencia}</span>
                </Badge>
                <Badge
                  variant="outline"
                  className="text-[10px] bg-white border-slate-200 text-slate-700"
                >
                  <Send className="h-3 w-3 mr-1 text-purple-500" />
                  Enviados <span className="font-semibold text-slate-900 ml-1">{metricas.qtd_enviados_agencia}</span>
                </Badge>
              </div>
            )}

            {/* Aprovado — projeto + freelancer */}
            {vaga.status === 'aprovado' && (
              projeto ? (
                <div className="space-y-1.5">
                  <div className="flex flex-wrap gap-1.5">
                    <Badge
                      variant="outline"
                      className={`text-[10px] ${PROJETO_STATUS_BADGE_CLASS[projeto.status]}`}
                    >
                      {CONTROLE_PROJETO_STATUS_LABEL[projeto.status]}
                    </Badge>
                    {projeto.valor_projeto != null && (
                      <Badge
                        variant="outline"
                        className="text-[10px] bg-white border-slate-200 text-slate-700"
                      >
                        <DollarSign className="h-3 w-3 mr-0.5 text-emerald-600" />
                        <span className="font-semibold text-slate-900">{formatReaisBRL(projeto.valor_projeto)}</span>
                      </Badge>
                    )}
                    {projeto.data_inicio && (
                      <Badge
                        variant="outline"
                        className="text-[10px] bg-white border-slate-200 text-slate-700"
                      >
                        <CalendarDays className="h-3 w-3 mr-1 text-slate-500" />
                        {formatDateBR(projeto.data_inicio)}
                      </Badge>
                    )}
                  </div>
                  {(projeto.freelancer_nome || freelancerAprovadoKanbanNome) && (
                    <div className="text-xs text-slate-700 flex items-center gap-1">
                      <User className="h-3 w-3 text-slate-500" />
                      <span className="truncate">
                        {projeto.freelancer_nome || freelancerAprovadoKanbanNome}
                      </span>
                      {projeto.freelancer_nome &&
                        freelancerAprovadoKanbanId &&
                        projeto.freelancer_id &&
                        freelancerAprovadoKanbanId !== projeto.freelancer_id && (
                          <Badge
                            variant="outline"
                            className="text-[10px] bg-red-50 text-red-700 border-red-200 ml-1"
                            title={`No kanban aprovado: ${freelancerAprovadoKanbanNome || 'outro freelancer'}`}
                          >
                            <AlertCircle className="h-3 w-3 mr-0.5" />
                            divergência
                          </Badge>
                        )}
                    </div>
                  )}
                  {proposta !== undefined && (
                    <div>
                      {proposta ? (
                        <Link
                          to={`/financeiro/propostas/${proposta.id}`}
                          onClick={(e) => e.stopPropagation()}
                          className="inline-flex items-center gap-1 text-[10px] font-medium text-primary hover:underline"
                        >
                          <FileText className="h-3 w-3" />
                          Proposta {proposta.codigo ?? "Sem código"} ·{" "}
                          {getPropostaStatusInfo(proposta.status).label}
                        </Link>
                      ) : (
                        <button
                          type="button"
                          disabled={criarProposta.isPending}
                          onClick={handleGerarProposta}
                          className="inline-flex items-center gap-1 text-[10px] font-medium text-slate-500 hover:text-slate-900"
                        >
                          <FileText className="h-3 w-3" />
                          Gerar proposta
                        </button>
                      )}
                    </div>
                  )}
                </div>
              ) : rascunhoCancelado ? (
                <div className="space-y-1.5">
                  <div className="flex flex-wrap items-center gap-1.5">
                    <Badge variant="outline" className="text-[10px] bg-red-50 text-red-700 border-red-200">
                      Projeto cancelado
                    </Badge>
                    <button
                      type="button"
                      disabled={voltarParaRascunho.isPending}
                      onClick={handleVoltarParaRascunho}
                      className="inline-flex items-center gap-1 text-[10px] font-medium text-slate-500 hover:text-slate-900 disabled:opacity-50"
                      data-testid="projeto-voltar-para-rascunho"
                    >
                      <FolderOpen className="h-3 w-3" />
                      Voltar para rascunho
                    </button>
                  </div>
                  {(rascunhoCancelado.freelancer_nome || freelancerAprovadoKanbanNome) && (
                    <div className="text-xs text-slate-700 flex items-center gap-1">
                      <User className="h-3 w-3 text-slate-500" />
                      <span className="truncate">
                        {rascunhoCancelado.freelancer_nome || freelancerAprovadoKanbanNome}
                      </span>
                    </div>
                  )}
                </div>
              ) : freelancerAprovadoKanbanNome ? (
                <div className="text-xs text-slate-700 flex items-center gap-1">
                  <User className="h-3 w-3 text-slate-500" />
                  <span className="truncate">{freelancerAprovadoKanbanNome}</span>
                </div>
              ) : (
                <div className="text-xs text-slate-500 italic">
                  Sem projeto vinculado
                </div>
              )
            )}
          </section>
        )}

        {/* Informações de Reprovação - Mostrar apenas se status for reprovado */}
        {vaga.status === 'reprovado' && (
          <div className="pt-2 border-t border-red-100" onClick={(e) => e.stopPropagation()}>
            {vaga.motivo_reprovacao && vaga.tipo_reprovacao ? (
              <div className="space-y-2">
                <div className="flex items-start gap-2">
                  <AlertCircle className="h-4 w-4 text-red-600 shrink-0 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium text-red-700 mb-1">
                      Motivo da Reprovação
                    </p>
                    <p className="text-xs text-slate-600 mb-1.5 leading-relaxed">
                      {getTipoReprovacaoLabel(vaga.tipo_reprovacao)}
                    </p>
                    <div className="bg-red-50 border border-red-100 rounded-md p-2">
                      <p className="text-xs text-slate-700 whitespace-pre-wrap wrap-break-word">
                        {vaga.motivo_reprovacao}
                      </p>
                    </div>
                    {vaga.reprovado_por && (
                      <Badge 
                        variant="outline" 
                        className={`mt-2 text-xs ${
                          vaga.reprovado_por === 'corisco' 
                            ? 'bg-blue-50 text-blue-700 border-blue-200' 
                            : 'bg-orange-50 text-orange-700 border-orange-200'
                        }`}
                      >
                        Reprovado por: {vaga.reprovado_por.toUpperCase()}
                      </Badge>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <div className="space-y-2">
                <div className="flex items-center gap-2 mb-2">
                  <AlertCircle className="h-4 w-4 text-amber-600 shrink-0" />
                  <p className="text-xs font-medium text-amber-700">
                    Motivo de reprovação não informado
                  </p>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  className="w-full h-8 text-xs border-red-200 hover:bg-red-50 hover:text-red-700 hover:border-red-300"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (onRequestReprovacao) {
                      onRequestReprovacao(vaga.id);
                    }
                  }}
                >
                  <FileText className="h-3 w-3 mr-1.5" />
                  Adicionar Motivo de Reprovação
                </Button>
              </div>
            )}
          </div>
        )}

        {/* Zona 4 — Responsável (sem border-t: a seção cinza acima já isola) */}
        <div onClick={(e) => e.stopPropagation()}>
          <Select
            value={vaga.responsavel || ""}
            onValueChange={onResponsavelChange}
          >
            <SelectTrigger className={`h-7 text-xs transition-colors ${
              responsavelUsuario 
                ? 'border-solid border-slate-300 bg-slate-50/50 hover:bg-slate-100' 
                : 'border-dashed border-slate-300 bg-transparent hover:bg-slate-50'
            }`}>
              <div className="flex items-center gap-2 w-full">
                <User className="h-3 w-3 shrink-0 text-slate-500" />
                <SelectValue 
                  placeholder="Atribuir responsável" 
                  className="truncate"
                >
                  {responsavelUsuario ? (
                    <span className="truncate">
                      {responsavelUsuario.full_name || responsavelUsuario.username}
                    </span>
                  ) : (
                    "Atribuir responsável"
                  )}
                </SelectValue>
              </div>
            </SelectTrigger>
            <SelectContent>
              {/* Opção para remover responsável */}
              {responsavelUsuario && (
                <>
                  <SelectItem value="__REMOVE__">
                    <span className="text-slate-500 italic">Remover responsável</span>
                  </SelectItem>
                  <div className="h-px bg-slate-200 my-1" />
                </>
              )}
              {usuarios.map((usuario) => (
                <SelectItem key={usuario.id} value={usuario.id}>
                  <div className="flex items-center gap-2">
                    <span>{usuario.full_name || usuario.username || usuario.id}</span>
                    {usuario.id === vaga.responsavel && (
                      <Badge variant="secondary" className="text-xs ml-auto">
                        Atual
                      </Badge>
                    )}
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
    </Card>
  );
}
